import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DownloadScheduleModalComponent } from '../download-schedule-modal/download-schedule-modal.component';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { DataService } from '@app-core/services/data/data.service';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TALK_TO_DATA_DATE_RANGES, DATE_RANGE_PRESET_TO_DAYS } from '@app-reports/constants/reports.constants';
import { ReportDomain } from '@app-reports/models/reports.model';

interface DownloadResponse {
  edited_sql: string;
}

interface Tag {
  tagId: number;
  tagName: string;
  attributeId?: number;
  attributeName?: string;
  attributeType?: string;
  id?: number;
  name?: string;
}

@Component({
  selector: 'app-download-custom-report-modal',
  templateUrl: './download-custom-report-modal.component.html',
  styleUrls: ['./download-custom-report-modal.component.scss'],
})
export class DownloadCustomReportModalComponent implements OnInit, OnDestroy {
  public downloadForm: FormGroup;
  public reportName: string;
  public reportDescription: string;
  public minDate: Date;
  public maxDate: Date;
  public unSelectAllTag = false;
  public entityType = ['asset', 'driver'];
  public selectedTagIds: number[] = [];
  public userTags: Tag[] = [];
  public attributeList = [];
  private allTags: Tag[] = [];
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private originalTagIds: number[] = [];
  private originalDays: number = 7;
  public downloadResponse: DownloadResponse;
  public edited_sql: string;
  public downloadReportLoader = false;
  private readonly DEFAULT_LIMIT = 100;
  private readonly DEFAULT_OFFSET = 0;
  public availableTags: string[] = [];
  public dateRangeOptions = TALK_TO_DATA_DATE_RANGES;
  public days: number;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<DownloadCustomReportModalComponent>,
    private taggingService: TaggingService,
    private reportService: ReportsService,
    private snackBarService: SnackBarService,
    private translate: TranslateService,
    public dataService: DataService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.reportName = data?.reportName || 'Custom Report';
    this.reportDescription = data.reportDescription || 'Select filters to customize your report before downloading';
  }

  ngOnInit(): void {
    this.initializeForm();
    this.getAttributeList();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private getAttributeList() {
    const params = {
      limit: this.DEFAULT_LIMIT,
      offset: this.DEFAULT_OFFSET,
    };
    this.taggingService
      .getAttributes(params)
      .pipe(
        finalize(() => {}),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any = {}) => {
          this.attributeList = res?.data;
          this.allTags = this.extractAllTags(this.attributeList);
          this.prefillFormData();
        },
        () => {}
      );
  }

  private extractAllTags(attributes: any[]): any[] {
    if (!attributes || !Array.isArray(attributes)) {
      return [];
    }
    const allTags = [];
    attributes.forEach((attribute) => {
      if (attribute.tags && Array.isArray(attribute.tags)) {
        attribute.tags.forEach((tag) => {
          allTags.push({
            ...tag,
            attributeId: attribute.attributeId,
            attributeName: attribute.attributeName,
            attributeType: attribute.attributeType,
          });
        });
      }
    });
    return allTags;
  }

  private initializeForm(): void {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - 7);

    this.downloadForm = this.fb.group({
      dateRangePreset: ['Past 7 days', Validators.required],
      dateRange: this.fb.group({
        start: [startDate, Validators.required],
        end: [today, Validators.required],
      }),
      tags: [[]],
    });
  }

  private prefillFormData(): void {
    if (!this.data || !this.data?.reportPayload) {
      return;
    }

    const { dateRange, tagIds } = this.data?.reportPayload || {};
    if (dateRange) {
      const isPreset = this.dateRangeOptions.some((o) => o.days === dateRange || o.label === dateRange);
      if (!isPreset) {
        this.dateRangeOptions = [{ label: dateRange, days: dateRange }, ...TALK_TO_DATA_DATE_RANGES];
      }
      this.originalDays = this.extractDaysFromRange(dateRange);
      this.downloadForm.patchValue({ dateRangePreset: dateRange });
      this.onDateRangePresetChange(dateRange);
    } else if (this.data.savedDateRange) {
      const isPreset = this.dateRangeOptions.some((o) => o.days === this.data.savedDateRange || o.label === this.data.savedDateRange);
      if (!isPreset) {
        this.dateRangeOptions = [{ label: this.data.savedDateRange, days: this.data.savedDateRange }, ...TALK_TO_DATA_DATE_RANGES];
      }
      this.originalDays = this.extractDaysFromRange(this.data.savedDateRange);
      this.downloadForm.patchValue({ dateRangePreset: this.data.savedDateRange });
      this.onDateRangePresetChange(this.data.savedDateRange);
    }

    if (tagIds && Array.isArray(tagIds) && tagIds.length > 0) {
      this.originalTagIds = [...tagIds];
      this.selectedTagIds = [...tagIds];
      this.userTags = this.getTagObjectsByIds(tagIds);
      this.downloadForm.patchValue({
        tags: tagIds,
      });
    } else if (this.data.tagIds && Array.isArray(this.data.tagIds) && this.data.tagIds.length > 0) {
      this.originalTagIds = [...this.data.tagIds];
      this.selectedTagIds = [...this.data.tagIds];
      this.userTags = this.getTagObjectsByIds(this.data.tagIds);
      this.downloadForm.patchValue({
        tags: this.data.tagIds,
      });
    }
  }

  private getTagObjectsByIds(tagIds: number[]): any[] {
    if (!tagIds || tagIds.length === 0) {
      return [];
    }
    const tagObjects: any[] = [];
    tagIds.forEach((tagId) => {
      const foundTag = this.allTags.find((tag) => tag.tagId === tagId);
      if (foundTag) {
        tagObjects.push(foundTag);
      }
    });
    return tagObjects;
  }
  private extractDaysFromRange(dateRangeValue: string): number {
    return DATE_RANGE_PRESET_TO_DAYS[dateRangeValue] ?? 7;
  }

  public onDateRangePresetChange(dateRangeValue: string): void {
    const today = new Date();
    const startDate = new Date();

    this.days = DATE_RANGE_PRESET_TO_DAYS[dateRangeValue] ?? 7;
    startDate.setDate(today.getDate() - this.days);

    this.downloadForm.patchValue({
      dateRange: {
        start: startDate,
        end: today,
      },
    });
  }

  public selectedTags(tags: number[]): void {
    this.selectedTagIds = tags;
    this.downloadForm.patchValue({ tags });
  }

  public onCancel(): void {
    this.dialogRef.close(null);
  }

  private haveFiltersChanged(selectedDateRangePreset: string, selectedTags: number[]): boolean {
    const selectedDays = this.extractDaysFromRange(selectedDateRangePreset);
    const daysChanged = this.originalDays !== selectedDays;
    const tagsChanged = JSON.stringify([...this.originalTagIds].sort()) !== JSON.stringify([...selectedTags].sort());
    return daysChanged || tagsChanged;
  }

  public onDownload(): void {
    if (this.downloadForm.valid) {
      const formValue = this.downloadForm.value;
      const selectedDateRangePreset = formValue.dateRangePreset;
      const selectedTags = this.selectedTagIds;
      const filtersChanged = this.haveFiltersChanged(selectedDateRangePreset, selectedTags);
      if (filtersChanged) {
        this.downloadWithFilterChanges(selectedDateRangePreset, selectedTags);
      } else {
        this.downloadWithoutFilterChanges();
      }
    }
  }

  private downloadWithFilterChanges(selectedDateRangePreset: string, selectedTags: number[]): void {
    const { reportName, fleetId, clientId, sql_where_date_columns } = this.data;
    const { sql } = this.data?.reportPayload || {};
    const selectedDays = this.extractDaysFromRange(selectedDateRangePreset);
    const domain: ReportDomain =
      this.data?.reportMetadata?.domain || this.data?.domain || this.data?.reportPayload?.metadata?.domain || 'trip';
    const reportSourceId: number = this.data?.reportMetadata?.reportSourceId ?? this.data?.reportSourceId ?? 1;
    const crossDomainPlan =
      this.data?.reportMetadata?.crossDomainPlan || this.data?.crossDomainPlan || this.data?.reportPayload?.metadata?.crossDomainPlan;
    const normalizedSql = this.toSqlArray(sql);

    const body = {
      customReportId: this.data.customReportId,
      reportName: reportName,
      fleetId: fleetId,
      clientId: clientId,
      sql: domain === 'cross_domain' ? normalizedSql : normalizedSql[0] ?? '',
      domain,
      ...(crossDomainPlan ? { crossDomainPlan } : {}),
      tagIds: selectedTags,
      days: selectedDays,
      sql_where_date_columns: sql_where_date_columns || [],
      ...(domain === 'cross_domain' ? {} : { reportSourceId }),
    };

    this.downloadReportLoader = true;

    this.reportService
      .downloadCustomReportListData(body)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (response: DownloadResponse) => {
          this.edited_sql = response?.edited_sql || '';

          const downloadBody = {
            fleetId: fleetId,
            customReportId: this.data.customReportId,
            sql: sql,
            reportType: 'CUSTOM_REPORT',
            reportName: reportName,
            reportDescription: this.reportDescription,
            tags: selectedTags,
            ...(this.edited_sql ? { reportQuery: this.edited_sql } : {}),
            ...(domain === 'cross_domain' ? {} : { reportSourceId: this.data?.reportSourceId ?? 1 }),
          };

          this.reportService
            .downloadCustomReport(downloadBody)
            .pipe(
              finalize(() => {
                this.downloadReportLoader = false;
              }),
              takeUntil(this.ngUnsubscribe)
            )
            .subscribe(
              () => {
                this.snackBarService.success(this.translate.instant('Report downloaded successfully'));
                this.proceedWithDownload();
              },
              (err: HttpErrorResponse) => {
                if (err?.status === 409) {
                  this.snackBarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
                } else {
                  this.snackBarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
                }
              }
            );
        },
        error: (err) => {
          this.downloadReportLoader = false;
          console.error('downloadCustomReportListData API Error:', err);
          this.snackBarService.failure(this.translate.instant('Failed to process report'));
        },
      });
  }

  private toSqlArray(sqlValue: string | string[]): string[] {
    if (Array.isArray(sqlValue)) {
      return sqlValue.filter((sql) => typeof sql === 'string' && sql.trim().length > 0);
    }
    if (typeof sqlValue === 'string' && sqlValue.trim().length > 0) {
      return [sqlValue];
    }
    return [];
  }

  private downloadWithoutFilterChanges(): void {
    const formValue = this.downloadForm.value;
    const dateRange = formValue.dateRange;
    const domain: ReportDomain =
      this.data?.reportMetadata?.domain || this.data?.domain || this.data?.reportPayload?.metadata?.domain || 'trip';
    const reportSourceId: number = this.data?.reportMetadata?.reportSourceId ?? this.data?.reportSourceId ?? 1;

    const downloadBody = {
      fleetId: this.data.fleetId,
      customReportId: this.data.customReportId,
      reportType: 'CUSTOM_REPORT',
      reportName: this.reportName,
      reportDescription: this.reportDescription,
      startDate: dateRange.start,
      endDate: dateRange.end,
      tags: this.selectedTagIds,
      ...(domain === 'cross_domain' ? {} : { reportSourceId }),
    };

    this.downloadReportLoader = true;

    this.reportService
      .downloadCustomReport(downloadBody)
      .pipe(
        finalize(() => {
          this.downloadReportLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.snackBarService.success(this.translate.instant('Report downloaded successfully'));
          this.proceedWithDownload();
        },
        (err: HttpErrorResponse) => {
          if (err?.status === 409) {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripDuplicate'));
          } else {
            this.snackBarService.failure(this.translate.instant('tripListComponentTripsDownloadFailure'));
          }
        }
      );
  }

  private proceedWithDownload(): void {
    const downloadParams = {
      tags: this.selectedTagIds,
      customReportId: this.data.customReportId,
      reportName: this.reportName,
    };

    this.dialogRef.close(downloadParams);

    const scheduleDialogRef = this.dialog.open(DownloadScheduleModalComponent, {
      width: '400px',
      disableClose: false,
      data: {
        reportName: this.reportName,
        downloadParams: downloadParams,
      },
    });

    scheduleDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        if (result.action === 'download_another') {
          this.dialog.open(DownloadCustomReportModalComponent, {
            width: '400px',
            disableClose: false,
            data: this.data,
          });
        }
      }
    });
  }
}
